import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Star, Plus, TrendingUp, TrendingDown, Trash2, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

export default function WatchlistPage() {
  const [watchlist, setWatchlist] = useState([]);
  const [tickerData, setTickerData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newSymbol, setNewSymbol] = useState({ symbol: '', name: '', notes: '' });

  const fetchWatchlist = useCallback(async () => {
    try {
      const response = await api.get('/api/watchlist');
      setWatchlist(response.data);
    } catch (error) {
      // Watchlist fetch failed silently
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTickerData = useCallback(async () => {
    try {
      const response = await api.get('/api/market/ticker');
      const map = {};
      response.data.data.forEach(item => { map[item.symbol] = item; });
      setTickerData(map);
    } catch (error) {
      // Ticker fetch failed silently
    }
  }, []);

  useEffect(() => {
    fetchWatchlist();
    fetchTickerData();
    const interval = setInterval(fetchTickerData, 30000);
    return () => clearInterval(interval);
  }, [fetchWatchlist, fetchTickerData]);

  const handleAddToWatchlist = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/watchlist', newSymbol);
      setNewSymbol({ symbol: '', name: '', notes: '' });
      setIsDialogOpen(false);
      fetchWatchlist();
    } catch (error) {
      // Add to watchlist failed silently
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    try {
      await api.delete(`/api/watchlist/${symbol}`);
      fetchWatchlist();
    } catch (error) {
      // Remove from watchlist failed silently
    }
  };

  const renderWatchlistContent = () => {
    if (loading) return <WatchlistLoading />;
    if (watchlist.length === 0) return <WatchlistEmpty onAdd={() => setIsDialogOpen(true)} />;
    return (
      <div className="bg-[#141417] border border-[#27272A] rounded-md overflow-hidden">
        <table className="data-table">
          <thead>
            <tr className="bg-[#09090B]">
              <th>Symbol</th>
              <th>Name</th>
              <th>Price</th>
              <th>Change</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {watchlist.map(item => {
              const ticker = tickerData[item.symbol];
              return (
                <tr key={item.id} data-testid={`watchlist-item-${item.symbol}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-[#007AFF]" />
                      <span className="font-mono font-bold text-white">{item.symbol}</span>
                    </div>
                  </td>
                  <td className="text-[#A1A1AA]">{item.name}</td>
                  <td className="font-mono text-white">
                    {ticker ? `$${ticker.price.toFixed(2)}` : '-'}
                  </td>
                  <td>
                    {ticker ? (
                      <div className={`flex items-center gap-1 ${ticker.change >= 0 ? 'ticker-up' : 'ticker-down'}`}>
                        {ticker.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        <span className="font-mono">
                          {ticker.change >= 0 ? '+' : ''}{ticker.change.toFixed(2)} ({ticker.change_percent.toFixed(2)}%)
                        </span>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="text-[#71717A] text-sm max-w-[200px] truncate">
                    {item.notes || '-'}
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFromWatchlist(item.symbol)}
                      className="text-[#FF3333] hover:text-[#FF3333] hover:bg-[#FF3333]/10"
                      data-testid={`remove-${item.symbol}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-8" data-testid="watchlist-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Trade Watchlist
          </h1>
          <p className="text-[#71717A]">Track your favorite stocks and ETFs</p>
        </div>
        <div className="flex items-center gap-4">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-watchlist-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Symbol
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141417] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-white">Add to Watchlist</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddToWatchlist} className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#A1A1AA]">Symbol</Label>
                  <Input
                    value={newSymbol.symbol}
                    onChange={(e) => setNewSymbol({ ...newSymbol, symbol: e.target.value.toUpperCase() })}
                    placeholder="e.g., AAPL"
                    className="input-dark mt-2"
                    required
                    data-testid="symbol-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Name</Label>
                  <Input
                    value={newSymbol.name}
                    onChange={(e) => setNewSymbol({ ...newSymbol, name: e.target.value })}
                    placeholder="e.g., Apple Inc."
                    className="input-dark mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Notes (optional)</Label>
                  <Textarea
                    value={newSymbol.notes}
                    onChange={(e) => setNewSymbol({ ...newSymbol, notes: e.target.value })}
                    placeholder="Your notes..."
                    className="input-dark mt-2"
                  />
                </div>
                <Button type="submit" className="btn-primary w-full">Add to Watchlist</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Link to="/">
            <Button variant="outline" className="border-[#27272A] text-[#A1A1AA]" data-testid="back-home-btn">
              <Home className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {renderWatchlistContent()}
    </div>
  );
}

function WatchlistLoading() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton h-20 rounded-md"></div>
      ))}
    </div>
  );
}

function WatchlistEmpty({ onAdd }) {
  return (
    <div className="bg-[#141417] border border-[#27272A] rounded-md p-12 text-center">
      <Star className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
      <h2 className="text-xl font-bold text-white mb-2">Your Watchlist is Empty</h2>
      <p className="text-[#71717A] mb-6">Start tracking your favorite stocks and ETFs</p>
      <Button className="btn-primary" onClick={onAdd}>
        <Plus className="w-4 h-4 mr-2" /> Add Your First Symbol
      </Button>
    </div>
  );
}
